import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import {
  Email as EmailIcon,
  ArrowForward,
  Android,
  Apple,
  Shield,
  VerifiedUser,
  Block,
} from '@mui/icons-material';

const contactCategories = [
  {
    icon: '🔄',
    title: 'Subscription Management',
    description: 'Need to cancel or downgrade your subscription? We\'re sad to see you go, but ready to help.',
    email: 'techsupport@cruvzmail.com',
  },
  {
    icon: '🎯',
    title: 'Demo & Onboarding',
    description: 'Looking for a demo, or have questions about our products and pricing? We\'d be happy to walk you through everything.',
    email: 'techsupport@cruvzmail.com',
  },
  {
    icon: '🚀',
    title: 'Migration Assistance',
    description: 'Planning to move from another email provider? Our team can help ensure a smooth and secure migration.',
    email: 'techsupport@cruvzmail.com',
  },
  {
    icon: '🤝',
    title: 'Partnerships & Reselling',
    description: 'Interested in partnering with us or offering our services under your brand (whitelabel)? Let\'s collaborate.',
    email: 'dev@cruvz.in',
  },
  {
    icon: '📢',
    title: 'Feedback & Suggestions',
    description: 'Have ideas to improve our services? We\'d love to hear your feedback.',
    email: 'dev@cruvz.in',
  },
  {
    icon: '💳',
    title: 'Billing & Payments',
    description: 'Questions about invoices, payments, or billing issues? Our team will clarify everything for you.',
    email: 'payments@cruvzmail.com',
  },
];

const navLinkSx = {
  fontSize: '0.875rem', fontWeight: 500, color: '#374151', textDecoration: 'none',
  cursor: 'pointer', transition: 'color 0.2s',
  '&:hover': { color: '#dc2626' },
};

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box sx={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', overflowX: 'hidden' }}>

      {/* ===== NAVBAR ===== */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => navigate('/login')}>
            <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: 77 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2.5, letterSpacing: '0.02em', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>Where Business Speaks!</Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/enterprise')}>For Business</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/features')}>Features</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/migration')}>Migration</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/signup')}>Plans and Pricing</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/help-center')}>Support</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button onClick={() => navigate('/login')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button onClick={() => navigate('/signup')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.25)',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', transform: 'translateY(-1px)' },
            }}>Get Started Free</Button>
          </Box>
        </Box>
      </Box>

      {/* ===== HERO SECTION ===== */}
      <Box sx={{
        pt: { xs: '120px', md: '130px' }, pb: { xs: 6, md: 10 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(168deg, #ffffff 0%, #fef7f7 30%, #fdf2f2 50%, #f8faff 100%)',
        position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: 'radial-gradient(circle, rgba(220,38,38,0.06) 0%, transparent 70%)', borderRadius: '50%' },
        '&::after': { content: '""', position: 'absolute', bottom: -100, left: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(59,130,246,0.04) 0%, transparent 70%)', borderRadius: '50%' },
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', alignItems: 'center', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 8 }, position: 'relative', zIndex: 1 }}>
          {/* Left - Text */}
          <Box sx={{ flex: 1, maxWidth: 560 }}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75,
              bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 100, mb: 3,
            }}>
              <Box sx={{ width: 6, height: 6, bgcolor: '#dc2626', borderRadius: '50%', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#dc2626' }}>We'd love to hear from you</Typography>
            </Box>
            <Typography sx={{
              fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, color: '#0f172a',
              lineHeight: 1.1, mb: 2.5, letterSpacing: '-0.03em',
            }}>
              Get in <Typography component="span" sx={{ color: '#dc2626', fontWeight: 800, fontSize: 'inherit' }}>Touch</Typography> with Us
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: '#64748b', lineHeight: 1.7, mb: 4 }}>
              Whether you need help, want a demo, or just want to say hello — our team is here for you. Choose the right department below and we'll get back to you promptly.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: '#dc2626', fontSize: 20 }} />
                <Typography sx={{ fontSize: '0.9rem', color: '#374151', fontWeight: 500 }}>techsupport@cruvzmail.com</Typography>
              </Box>
            </Box>
          </Box>

          {/* Right - Model Image */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', maxWidth: { xs: 350, md: 460 } }}>
            <Box sx={{
              position: 'relative', width: '100%',
              '&::before': {
                content: '""', position: 'absolute', top: -15, right: -15, bottom: -15, left: -15,
                background: 'linear-gradient(135deg, rgba(220,38,38,0.15) 0%, rgba(59,130,246,0.08) 100%)',
                borderRadius: '24px', zIndex: 0,
              },
            }}>
              <Box
                component="img"
                src="/Model01.png"
                alt="CruvzMail Support"
                sx={{
                  width: '100%', height: 'auto', borderRadius: '20px',
                  position: 'relative', zIndex: 1,
                  boxShadow: '0 25px 60px rgba(0,0,0,0.15), 0 8px 20px rgba(0,0,0,0.08)',
                  objectFit: 'cover', maxHeight: 500,
                }}
              />
              {/* Floating badge */}
              <Box sx={{
                position: 'absolute', bottom: 20, left: -20, zIndex: 2,
                bgcolor: '#fff', borderRadius: '16px', px: 2.5, py: 1.5,
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '1.2rem' }}>🎯</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>Response Time</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>Under 24 hours</Typography>
                </Box>
              </Box>
              {/* Floating badge top-right */}
              <Box sx={{
                position: 'absolute', top: 20, right: -20, zIndex: 2,
                bgcolor: '#fff', borderRadius: '16px', px: 2.5, py: 1.5,
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: '1.2rem' }}>✅</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>24/7 Support</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>Always available</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== CONTACT CARDS ===== */}
      <Box sx={{
        py: { xs: 6, md: 10 }, px: { xs: 2, md: 5 },
        bgcolor: '#fff',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography sx={{
              fontSize: { xs: '1.5rem', md: '2.25rem' }, fontWeight: 800, color: '#0f172a',
              letterSpacing: '-0.02em', mb: 1.5,
            }}>
              How Can We Help?
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#64748b', maxWidth: 600, mx: 'auto', lineHeight: 1.7 }}>
              Select the department that best matches your inquiry. Our dedicated teams are ready to assist you.
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 3,
          }}>
            {contactCategories.map((cat, index) => (
              <Box
                key={index}
                component="a"
                href={`mailto:${cat.email}`}
                sx={{
                  textDecoration: 'none',
                  p: 4,
                  borderRadius: '16px',
                  border: '1.5px solid #f1f5f9',
                  bgcolor: '#fff',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  display: 'flex', flexDirection: 'column',
                  '&:hover': {
                    borderColor: '#fecaca',
                    boxShadow: '0 12px 40px rgba(220,38,38,0.08), 0 4px 12px rgba(0,0,0,0.04)',
                    transform: 'translateY(-4px)',
                    '& .card-icon': { transform: 'scale(1.1)' },
                    '& .card-arrow': { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box className="card-icon" sx={{
                    width: 56, height: 56, borderRadius: '14px',
                    bgcolor: '#fef2f2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.75rem', transition: 'transform 0.3s',
                  }}>
                    {cat.icon}
                  </Box>
                  <ArrowForward className="card-arrow" sx={{ color: '#dc2626', fontSize: 20, opacity: 0, transform: 'translateX(-8px)', transition: 'all 0.3s' }} />
                </Box>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', mb: 1 }}>
                  {cat.title}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6, mb: 2.5, flex: 1 }}>
                  {cat.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <EmailIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#dc2626' }}>
                    {cat.email}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ===== CTA SECTION ===== */}
      <Box sx={{
        py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -100, right: -100, width: 400, height: 400, background: 'radial-gradient(circle, rgba(220,38,38,0.12) 0%, transparent 70%)', borderRadius: '50%' },
      }}>
        <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: '#fff', mb: 2, letterSpacing: '-0.02em' }}>
            Ready to get started?
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', mb: 4, lineHeight: 1.7 }}>
            Join thousands of businesses that trust CruvzMail for their professional email communication.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} sx={{
              px: 4, py: 1.5, borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 4px 14px rgba(220,38,38,0.3)',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 6px 20px rgba(220,38,38,0.4)', transform: 'translateY(-2px)' },
              transition: 'all 0.3s',
            }}>
              Start Free Trial
            </Button>
            <Button onClick={() => navigate('/login')} sx={{
              px: 4, py: 1.5, borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, textTransform: 'none',
              color: '#fff', border: '1.5px solid rgba(255,255,255,0.2)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' },
              transition: 'all 0.3s',
            }}>
              Back to Home
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ===== FOOTER ===== */}
      <Box sx={{ bgcolor: '#0a0f1a', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3), rgba(59,130,246,0.3), transparent)' },
      }}>
        {/* Top CTA strip */}
        <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(15,23,42,0) 50%, rgba(59,130,246,0.06) 100%)' }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.02em' }}>Ready to upgrade your business email?</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>Join thousands of businesses already using CruvzMail. Free plan available.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button onClick={() => navigate('/signup')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                boxShadow: '0 4px 20px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #ef4444, #dc2626)', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(220,38,38,0.4)' },
                transition: 'all 0.3s',
              }}>Start Free Trial</Button>
              <Button onClick={() => navigate('/contact')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600, textTransform: 'none',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.3s',
              }}>Contact Sales</Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Main footer content */}
        <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 6, lg: 10 } }}>
            <Box sx={{ flex: '0 0 320px', maxWidth: { xs: '100%', lg: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box component="img" src="/Cruvzmail_Footer_logo.png" alt="CruvzMail" sx={{ height: 80, alignSelf: 'flex-start' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2, letterSpacing: '0.02em' }}>Where Business Speaks!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 3 }}>
                Professional email hosting built for modern businesses. Self-hosted, secure, and fully customizable with your brand.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <Button href="/CruvzMail-v1.0.apk" download sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'all 0.25s', display: 'flex', alignItems: 'center', gap: 1,
                }} startIcon={<Android sx={{ fontSize: 20 }} />}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>Download on</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Android</Typography>
                  </Box>
                </Button>
                <Box sx={{ px: 2.5, py: 1.25, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5 }}>
                  <Apple sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>Coming soon</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1.2 }}>iOS</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: '256-bit encryption' },
                  { icon: <VerifiedUser sx={{ fontSize: 14 }} />, text: '99.9% uptime' },
                  { icon: <Block sx={{ fontSize: 14 }} />, text: 'Zero ads' },
                ].map((badge) => (
                  <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& svg': { color: 'rgba(255,255,255,0.25)' } }}>
                    {badge.icon}
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>{badge.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 4, md: 3 } }}>
              {[
                { title: 'Product', links: [
                  { label: 'Features', href: '/features' }, { label: 'Pricing', href: '/signup' },
                  { label: 'Enterprise', href: '/enterprise' }, { label: 'Migration', href: '/migration' },
                  { label: 'Security', href: '/features' }, { label: 'Mobile App', href: '/CruvzMail-v1.0.apk' },
                ]},
                { title: 'Solutions', links: [
                  { label: 'Small Business', href: '/signup' }, { label: 'Enterprise', href: '/enterprise' },
                  { label: 'Startups', href: '/signup' }, { label: 'Education', href: '/signup' },
                  { label: 'Healthcare', href: '/signup' }, { label: 'Non-Profit', href: '/signup' },
                ]},
                { title: 'Company', links: [
                  { label: 'About Us', href: '/about' }, { label: 'Blog', href: '/blog' },
                  { label: 'Careers', href: '/careers' }, { label: 'Contact', href: '/contact' },
                  { label: 'Press Kit', href: '/about' }, { label: 'Partners', href: '/contact' },
                ]},
                { title: 'Resources', links: [
                  { label: 'Help Center', href: '/help-center' }, { label: 'Documentation', href: '/documentation' },
                  { label: 'API Reference', href: '/documentation' }, { label: 'System Status', href: '/status' },
                  { label: 'Community', href: '/community' }, { label: 'Webinars', href: '/blog' },
                ]},
              ].map((col) => (
                <Box key={col.title}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5 }}>{col.title}</Typography>
                  {col.links.map((link) => (
                    <Typography key={link.label} component="a" href={link.href}
                      onClick={(e: React.MouseEvent) => { if (!link.href.endsWith('.apk')) { e.preventDefault(); navigate(link.href); } }}
                      sx={{ display: 'block', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', mb: 1.5, transition: 'all 0.2s', '&:hover': { color: '#fca5a5', transform: 'translateX(2px)' } }}
                    >{link.label}</Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2.5 }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} CruvzMail by Cruvz Online Shopping India Pvt Ltd. All rights reserved.</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
                { label: 'SLA', href: '/terms-of-service' },
                { label: 'Sitemap', href: '/features' },
              ].map((l, i) => (
                <React.Fragment key={l.label}>
                  {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />}
                  <Typography component="a" href={l.href} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(l.href); }} sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>{l.label}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>All systems operational</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactPage;
